import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  IconButton,
  TextField,
  Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import EventNoteOutlinedIcon from '@material-ui/icons/EventNoteOutlined';

import MainContainer from '../../components/MainContainer';
import { EmptyState, PageHeader, Section, StatusBadge } from '../../components/ui';
import { getTokens } from '../../styles/tokens';

const useStyles = makeStyles(theme => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
    },
    composer: { marginBottom: 16 },
    composerRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      [theme.breakpoints.down('xs')]: {
        alignItems: 'stretch',
        flexDirection: 'column',
      },
    },
    input: { flex: 1, minWidth: 0 },
    addButton: { flex: 'none' },
    listContainer: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      ...theme.scrollbarStyles,
    },
    list: { listStyle: 'none', margin: 0, padding: 0 },
    listItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      minHeight: 60,
      padding: '10px 12px 10px 20px',
      borderBottom: '1px solid ' + tk.lineRow,
      transition: 'background-color ' + tk.motion.fast,
      '&:last-child': { borderBottom: 'none' },
      '&:hover': { backgroundColor: tk.rowHover },
    },
    editingItem: {
      backgroundColor: tk.tint.soft,
      '&:hover': { backgroundColor: tk.tint.soft },
    },
    itemText: { flex: 1, minWidth: 0 },
    itemPrimary: {
      color: tk.text,
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.4,
      overflowWrap: 'anywhere',
    },
    itemSecondary: {
      marginTop: 2,
      color: tk.textMuted,
      fontSize: 12,
    },
    itemActions: { flex: 'none', display: 'flex', alignItems: 'center', gap: 2 },
    deleteButton: { color: tk.semantic.error },
  };
});

const ToDoList = () => {
  const classes = useStyles();
  const [task, setTask] = useState('');
  const [tasks, setTasks] = useState([]);
  const [editIndex, setEditIndex] = useState(-1);

  useEffect(() => {
    const savedTasks = localStorage.getItem('tasks');
    if (savedTasks) setTasks(JSON.parse(savedTasks));
  }, []);

  useEffect(() => {
    localStorage.setItem('tasks', JSON.stringify(tasks));
  }, [tasks]);

  const handleAddTask = () => {
    if (!task.trim()) return;
    const now = new Date();
    if (editIndex >= 0) {
      const newTasks = [...tasks];
      newTasks[editIndex] = {
        text: task,
        updatedAt: now,
        createdAt: newTasks[editIndex].createdAt,
      };
      setTasks(newTasks);
      setEditIndex(-1);
    } else {
      setTasks([...tasks, { text: task, createdAt: now, updatedAt: now }]);
    }
    setTask('');
  };

  const handleEditTask = index => {
    setTask(tasks[index].text);
    setEditIndex(index);
  };

  const handleDeleteTask = index => {
    const newTasks = [...tasks];
    newTasks.splice(index, 1);
    setTasks(newTasks);
    if (editIndex === index) {
      setTask('');
      setEditIndex(-1);
    }
  };

  return (
    <MainContainer maxWidth={1080}>
      <div className={classes.root}>
        <PageHeader
          title="Minhas tarefas"
          subtitle="Organize lembretes rápidos para acompanhar sua rotina de atendimento."
          badge={
            <StatusBadge color="neutral" dot={false} label={String(tasks.length)} />
          }
        />
        <Section className={classes.composer}>
          <div className={classes.composerRow}>
            <TextField
              className={classes.input}
              placeholder="Descreva uma nova tarefa"
              value={task}
              onChange={event => setTask(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') handleAddTask();
              }}
              variant="outlined"
              size="small"
              inputProps={{ 'aria-label': 'Nova tarefa' }}
            />
            <Button
              variant="contained"
              color="primary"
              className={classes.addButton}
              onClick={handleAddTask}
              startIcon={editIndex >= 0 ? null : <AddIcon />}
            >
              {editIndex >= 0 ? 'Salvar alterações' : 'Adicionar tarefa'}
            </Button>
          </div>
        </Section>
        <Section flush className={classes.listContainer}>
          {tasks.length === 0 ? (
            <EmptyState
              icon={<EventNoteOutlinedIcon />}
              title="Nenhuma tarefa por aqui"
              message="Adicione seu primeiro lembrete para manter as próximas ações visíveis."
            />
          ) : (
            <ul className={classes.list}>
              {tasks.map((item, index) => (
                <li
                  key={index}
                  className={[
                    classes.listItem,
                    editIndex === index ? classes.editingItem : '',
                  ].join(' ')}
                >
                  <div className={classes.itemText}>
                    <div className={classes.itemPrimary}>{item.text}</div>
                    <div className={classes.itemSecondary}>
                      {'Atualizada em ' +
                        new Date(item.updatedAt).toLocaleString('pt-BR')}
                    </div>
                  </div>
                  <div className={classes.itemActions}>
                    <Tooltip title="Editar tarefa">
                      <IconButton
                        size="small"
                        onClick={() => handleEditTask(index)}
                        aria-label="Editar tarefa"
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir tarefa">
                      <IconButton
                        size="small"
                        className={classes.deleteButton}
                        onClick={() => handleDeleteTask(index)}
                        aria-label="Excluir tarefa"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </MainContainer>
  );
};

export default ToDoList;
